import {
  IPatient,
  IPractice,
  IScribeNote,
  IScribeNoteRecovery,
  IScribeSession,
  IStaffer,
  PatientCollection,
  ScribeNoteRecoveryStatus,
  ScribeNoteType,
  ScribeSessionCollection,
  ScribeSessionSource,
  ScribeSessionStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  CollectionReference,
  DocumentReference,
  Firestore,
  IReffable,
  ISODateType,
  ITimestampRange,
  Query,
  Timestamp,
  Transaction,
  WithRef,
  collectionGroupQuery,
  hardDeleteDoc,
  isSameRef,
  limit,
  multiUndeleted,
  orderBy,
  subCollection,
  toMoment,
  toTimestamp,
  where,
} from '@principle-theorem/shared';
import { sumBy } from 'lodash';
import { Observable } from 'rxjs';
import { ScribeLease } from './scribe-lease';
import { ScribeNote } from './scribe-note';

export interface IAcquireLeaseResult {
  acquired: boolean;
}

/** The outcome of one recovery sweep attempt, as recorded on the session. */
export interface IScribeNoteRecoveryAttempt {
  /** Stored as `noteRecovery.lastResult`. */
  lastResult: string;
  /** When to try again; when omitted, the previous schedule is kept. */
  nextAttemptAt?: Timestamp;
  /** Ends the recovery instead of scheduling another attempt. */
  exhausted: boolean;
}

export interface IScribeSessionCounterSummary {
  minutesRecorded: number;
  notesGenerated: number;
  notesInserted: number;
  insertedPercent: number;
  estTimeSavedMinutes: number;
}

/** Cadence a live session re-writes its lease heartbeat at. */
export const SCRIBE_HEARTBEAT_INTERVAL_SECONDS = 30;
/** A lease untouched for ~4 missed heartbeats is stale and can be taken over. */
export const SCRIBE_LEASE_STALE_THRESHOLD_SECONDS = 120;
/**
 * Delay before the recovery sweep's first fetch. The slowest stop-to-note time
 * VoxDenta reported was 4 m 50 s (28 Oct 2026), so the note is usually ready
 * by then.
 */
export const SCRIBE_NOTE_RECOVERY_FIRST_DELAY_MINUTES = 5;
/**
 * The recovery sweep gives up this long after the session stopped (or was
 * created, when no stop was recorded), so a session whose attempts keep
 * failing still ends.
 */
export const SCRIBE_NOTE_RECOVERY_GIVE_UP_HOURS = 24;
/**
 * Allowance when comparing a VoxDenta note's server `updatedAt` with the
 * client-stamped stop instant: 2 minutes absorbs a clinic PC clock running
 * fast. Interim notes for recordings over 42 minutes are generated well before
 * stop, so this only admits one for recordings of 42-44 minutes, which
 * VoxDenta's 2 Nov 2026 fix removes. The recorder and the recovery sweep share
 * it so both apply the same rule.
 */
export const SCRIBE_VOX_CLOCK_SKEW_MS = 120_000;
/**
 * Each recovery can wait 15 s for a token and 15 s for the notes, so 5 per
 * sweep run (150 s) stay inside the sweep's 240 s timeout.
 */
const noteRecoveriesPerSweep = 5;
const estimatedMinutesSavedPerNote = 5;

export class ScribeSession {
  static init(
    overrides: AtLeast<
      IScribeSession,
      'stafferRef' | 'practiceRef' | 'brandRef' | 'source' | 'recordDate'
    >
  ): IScribeSession {
    return {
      status: ScribeSessionStatus.InProgress,
      noteType: ScribeNoteType.Dentist,
      deleted: false,
      ...overrides,
    };
  }

  static col(
    patient: IReffable<IPatient>
  ): CollectionReference<IScribeSession> {
    return subCollection<IScribeSession>(
      patient.ref,
      PatientCollection.ScribeSessions
    );
  }

  static all$(
    patient: IReffable<IPatient>
  ): Observable<WithRef<IScribeSession>[]> {
    return Firestore.query$(ScribeSession.col(patient)).pipe(multiUndeleted());
  }

  static async create(
    patient: IReffable<IPatient>,
    session: IScribeSession
  ): Promise<DocumentReference<IScribeSession>> {
    return Firestore.addDoc(ScribeSession.col(patient), session);
  }

  static notesCol(
    session: IReffable<IScribeSession>
  ): CollectionReference<IScribeNote> {
    return subCollection<IScribeNote>(
      session.ref,
      ScribeSessionCollection.Notes
    );
  }

  static async addNote(
    session: IReffable<IScribeSession>,
    note: AtLeast<IScribeNote, 'content'>
  ): Promise<DocumentReference<IScribeNote>> {
    return Firestore.addDoc(
      ScribeSession.notesCol(session),
      ScribeNote.init(note)
    );
  }

  static byPractice$(
    practiceRef: DocumentReference<IPractice>
  ): Observable<WithRef<IScribeSession>[]> {
    return Firestore.query$(
      collectionGroupQuery<IScribeSession>(
        PatientCollection.ScribeSessions,
        where('deleted', '==', false),
        where('practiceRef', '==', practiceRef),
        orderBy('createdAt', 'desc')
      )
    );
  }

  /**
   * The dashboard's ranged/filtered session feed as a composable Firestore
   * `Query` (not an Observable) so the caller can layer `paginatedQuery$` over
   * it for infinite scroll. Collection-group scoped across every patient's
   * `scribeSessions`, narrowed to one practice and a `createdAt` window, and
   * optionally to a single staffer. Sorted `createdAt desc` to match the
   * pagination sort so the two compose without a re-sort.
   */
  static byPracticeInRangeQuery(
    practiceRef: DocumentReference<IPractice>,
    range: ITimestampRange,
    stafferRef?: DocumentReference<IStaffer>
  ): Query<IScribeSession> {
    return collectionGroupQuery<IScribeSession>(
      PatientCollection.ScribeSessions,
      where('deleted', '==', false),
      where('practiceRef', '==', practiceRef),
      ...(stafferRef ? [where('stafferRef', '==', stafferRef)] : []),
      where('createdAt', '>=', range.from),
      where('createdAt', '<=', range.to),
      orderBy('createdAt', 'desc')
    );
  }

  /**
   * All of a patient's `ready` sessions for one staffer on `recordDate` — the
   * source for the launcher's badge and Record/View menu, and for the review
   * dialog. Scoped to the patient's own `scribeSessions` subcollection (not the
   * collection group).
   * Keying on `recordDate` equality rather than a `createdAt` range matches the
   * chart day the clinical notes list is already showing, and leaves the query
   * free of inequalities.
   * Fully-inserted sessions are returned too, so downstream counts include
   * already-inserted notes and those notes stay available for re-insertion.
   */
  static readyForContext$(
    patient: IReffable<IPatient>,
    stafferRef: DocumentReference<IStaffer>,
    recordDate: ISODateType
  ): Observable<WithRef<IScribeSession>[]> {
    return Firestore.query$(
      ScribeSession.col(patient),
      where('deleted', '==', false),
      where('recordDate', '==', recordDate),
      where('stafferRef', '==', stafferRef),
      where('status', '==', ScribeSessionStatus.Ready),
      orderBy('createdAt', 'desc')
    );
  }

  /**
   * Every `ready` session the patient has, in ONE patient-scoped query, for
   * surfaces that must show a count against many staffer/day keys at once. The
   * clinical-note history renders a row per note in the patient's entire
   * history, so resolving each row with its own {@link readyForContext$} would
   * open one live listener per note; callers subscribe here once and bucket the
   * result with {@link readyNoteCountsByContext}.
   *
   * Deliberately unordered — callers reduce to counts, so a sort would only
   * widen the composite index for nothing.
   */
  static readyForPatient$(
    patient: IReffable<IPatient>
  ): Observable<WithRef<IScribeSession>[]> {
    return Firestore.query$(
      ScribeSession.col(patient),
      where('deleted', '==', false),
      where('status', '==', ScribeSessionStatus.Ready)
    );
  }

  /**
   * The key a session's notes are counted under: one clinician on one charted
   * day. Shared by both sides of {@link readyNoteCountsByContext} so a lookup
   * can never be keyed differently from the bucket it is looking for.
   */
  static contextKey(
    stafferRef: DocumentReference<IStaffer>,
    recordDate: ISODateType
  ): string {
    return `${stafferRef.path}|${recordDate}`;
  }

  /**
   * Buckets sessions into `notesGenerated` per {@link contextKey}, letting a
   * caller resolve many staffer/day counts from a single
   * {@link readyForPatient$} emission. Keys absent from the map produced no
   * notes.
   */
  static readyNoteCountsByContext(
    sessions: IScribeSession[]
  ): Map<string, number> {
    const grouped = sessions.reduce((groups, session) => {
      const key = ScribeSession.contextKey(
        session.stafferRef,
        session.recordDate
      );
      return groups.set(key, [...(groups.get(key) ?? []), session]);
    }, new Map<string, IScribeSession[]>());

    return new Map(
      Array.from(grouped, ([key, group]) => [
        key,
        ScribeSession.counterSummary(group).notesGenerated,
      ])
    );
  }

  /**
   * Completes a session. Refuses a `failed` -> `ready` transition: once the
   * lease-driven sweep has failed a session it must not silently resurrect.
   * `durationSeconds` is client-measured and analytics-only.
   */
  static async markReady(
    session: IReffable<IScribeSession>,
    durationSeconds?: number
  ): Promise<void> {
    await Firestore.runTransaction(async (transaction) => {
      const current = await Firestore.getDoc(session.ref, transaction);
      if (current.status === ScribeSessionStatus.Failed) {
        throw new Error(
          `Cannot mark a failed scribe session as ready: ${session.ref.path}`
        );
      }
      await Firestore.patchDoc(
        session.ref,
        {
          status: ScribeSessionStatus.Ready,
          ...(durationSeconds !== undefined ? { durationSeconds } : {}),
        },
        transaction
      );
    });
  }

  static async markFailed(session: IReffable<IScribeSession>): Promise<void> {
    await Firestore.patchDoc(session.ref, {
      status: ScribeSessionStatus.Failed,
    });
  }

  static async touchHeartbeat(
    session: IReffable<IScribeSession>
  ): Promise<void> {
    await Firestore.patchDoc(session.ref, { lastActiveAt: toTimestamp() });
  }

  /**
   * Stores the VoxDenta session path as soon as the session exists, so the
   * recovery sweep can fetch the note even if the client never comes back.
   */
  static async setVoxSessionPath(
    session: IReffable<IScribeSession>,
    voxSessionPath: string
  ): Promise<void> {
    await Firestore.patchDoc(session.ref, { voxSessionPath });
  }

  /**
   * Stores the client instant `stop()` was called: a fetched note last updated
   * before it is an interim note.
   */
  static async markStopped(
    session: IReffable<IScribeSession>,
    stoppedAt: Timestamp
  ): Promise<void> {
    await Firestore.patchDoc(session.ref, { stoppedAt });
  }

  /**
   * Transactionally claims the staffer's single-session lease for
   * `newSessionRef`. Refuses (writes nothing) when a fresh lease is held by a
   * different session; takes over a stale lease, failing the displaced
   * in-progress session in the same transaction so a cross-tab orphan is never
   * stranded.
   */
  static async acquireLease(
    staffer: IReffable<IStaffer>,
    newSessionRef: DocumentReference<IScribeSession>
  ): Promise<IAcquireLeaseResult> {
    const leaseRef = ScribeLease.doc(staffer);
    return Firestore.runTransaction(async (transaction) => {
      const now = toTimestamp();
      const lease = await Firestore.safeGetDoc(leaseRef, transaction);
      const leaseIsFresh =
        lease !== undefined &&
        ScribeSession._leaseIsFresh(lease.lastActiveAt, now);
      const displacedSession =
        lease !== undefined && !leaseIsFresh
          ? await Firestore.safeGetDoc(lease.sessionRef, transaction)
          : undefined;

      if (
        leaseIsFresh &&
        lease !== undefined &&
        !isSameRef(lease.sessionRef, newSessionRef)
      ) {
        return { acquired: false };
      }

      if (
        displacedSession !== undefined &&
        displacedSession.status === ScribeSessionStatus.InProgress
      ) {
        await ScribeSession._failAbandonedSession(
          displacedSession,
          now,
          transaction
        );
      }

      await Firestore.patchDoc(
        leaseRef,
        { sessionRef: newSessionRef, lastActiveAt: now },
        transaction
      );
      return { acquired: true };
    });
  }

  static async releaseLease(staffer: IReffable<IStaffer>): Promise<void> {
    await hardDeleteDoc(ScribeLease.doc(staffer));
  }

  /**
   * Fails the abandoned live session and clears the lease when the staffer's
   * lease has gone stale. Runs in a transaction so it can never delete a lease
   * a concurrent {@link acquireLease} just claimed: the lease is re-read inside
   * the transaction, and one that is now absent or fresh aborts the sweep
   * before any write. Lease-doc-only by design: the single-session invariant
   * means only the lease-referenced session can be a live `in-progress`
   * session, so no collection-group scan is needed.
   */
  static async sweepStale(staffer: IReffable<IStaffer>): Promise<void> {
    const leaseRef = ScribeLease.doc(staffer);
    await Firestore.runTransaction(async (transaction) => {
      const now = toTimestamp();
      const lease = await Firestore.safeGetDoc(leaseRef, transaction);
      if (
        lease === undefined ||
        ScribeSession._leaseIsFresh(lease.lastActiveAt, now)
      ) {
        return;
      }
      const session = await Firestore.safeGetDoc(lease.sessionRef, transaction);
      if (
        session !== undefined &&
        session.status === ScribeSessionStatus.InProgress
      ) {
        await ScribeSession._failAbandonedSession(session, now, transaction);
      }
      transaction.delete(leaseRef);
    });
  }

  /**
   * A new `pending` recovery, first due
   * {@link SCRIBE_NOTE_RECOVERY_FIRST_DELAY_MINUTES} after `now`. Always the
   * whole object, so a repeated request resets an earlier recovery's attempts.
   */
  static pendingRecovery(now: Timestamp): IScribeNoteRecovery {
    return {
      status: ScribeNoteRecoveryStatus.Pending,
      attempts: 0,
      nextAttemptAt: toTimestamp(
        toMoment(now).add(SCRIBE_NOTE_RECOVERY_FIRST_DELAY_MINUTES, 'minutes')
      ),
      lastResult: 'requested',
    };
  }

  /**
   * Hands the session to the recovery sweep, which fetches its note from
   * VoxDenta, after the client stopped waiting without one.
   */
  static async requestNoteRecovery(
    session: IReffable<IScribeSession>
  ): Promise<void> {
    await Firestore.patchDoc(session.ref, {
      noteRecovery: ScribeSession.pendingRecovery(toTimestamp()),
    });
  }

  /**
   * Records one recovery sweep attempt: counts it, then either schedules the
   * next attempt or exhausts the recovery.
   */
  static async recordRecoveryAttempt(
    session: WithRef<IScribeSession>,
    result: IScribeNoteRecoveryAttempt
  ): Promise<void> {
    await Firestore.patchDoc(session.ref, {
      noteRecovery: {
        status: result.exhausted
          ? ScribeNoteRecoveryStatus.Exhausted
          : ScribeNoteRecoveryStatus.Pending,
        attempts: (session.noteRecovery?.attempts ?? 0) + 1,
        nextAttemptAt:
          result.nextAttemptAt ??
          session.noteRecovery?.nextAttemptAt ??
          toTimestamp(),
        lastResult: result.lastResult,
      },
    });
  }

  /**
   * Readies a session whose note was fetched from VoxDenta after the client
   * gave up. This is the only sanctioned `failed` -> `ready` transition;
   * {@link markReady} keeps refusing it. A soft-deleted session is left as is.
   */
  static async markRecovered(
    session: IReffable<IScribeSession>
  ): Promise<void> {
    await Firestore.runTransaction(async (transaction) => {
      const current = await Firestore.getDoc(session.ref, transaction);
      if (current.deleted) {
        return;
      }
      const noteRecovery = current.noteRecovery
        ? {
            ...current.noteRecovery,
            status: ScribeNoteRecoveryStatus.Recovered,
          }
        : undefined;
      await Firestore.patchDoc(
        session.ref,
        { status: ScribeSessionStatus.Ready, noteRecovery },
        transaction
      );
    });
  }

  /**
   * The recovery sweep's work list: due pending recoveries across every
   * patient's sessions, earliest first. Soft-deleted sessions are not filtered
   * out here; {@link claimRecoveryAttempt} exhausts them so they cannot stay
   * due.
   */
  static recoveryDueQuery(now: Timestamp): Query<IScribeSession> {
    return collectionGroupQuery<IScribeSession>(
      PatientCollection.ScribeSessions,
      where('noteRecovery.status', '==', ScribeNoteRecoveryStatus.Pending),
      where('noteRecovery.nextAttemptAt', '<=', now),
      orderBy('noteRecovery.nextAttemptAt', 'asc'),
      limit(noteRecoveriesPerSweep)
    );
  }

  /**
   * Claims a due recovery for one sweep run by pushing `nextAttemptAt` out to
   * `leaseUntil`, which stops an overlapping run from fetching and adding the
   * same note twice. Returns the claimed session, or `undefined` when there is
   * nothing to attempt. A due recovery on a soft-deleted session, or one past
   * {@link SCRIBE_NOTE_RECOVERY_GIVE_UP_HOURS}, is exhausted instead: a deleted
   * session would otherwise stay due forever and crowd out the sweep's batch,
   * and a session whose attempts keep crashing must still end.
   */
  static async claimRecoveryAttempt(
    session: IReffable<IScribeSession>,
    leaseUntil: Timestamp
  ): Promise<WithRef<IScribeSession> | undefined> {
    return Firestore.runTransaction(async (transaction) => {
      const now = toTimestamp();
      const current = await Firestore.getDoc(session.ref, transaction);
      const recovery = current.noteRecovery;
      if (!recovery || !ScribeSession._recoveryIsDue(recovery, now)) {
        return;
      }
      const endReason = ScribeSession._recoveryEndReason(current, now);
      if (endReason) {
        await Firestore.patchDoc(
          session.ref,
          {
            noteRecovery: {
              ...recovery,
              status: ScribeNoteRecoveryStatus.Exhausted,
              lastResult: endReason,
            },
          },
          transaction
        );
        return;
      }
      const claimed: IScribeNoteRecovery = {
        ...recovery,
        nextAttemptAt: leaseUntil,
      };
      await Firestore.patchDoc(
        session.ref,
        { noteRecovery: claimed },
        transaction
      );
      return { ...current, noteRecovery: claimed };
    });
  }

  /**
   * Aggregates the function-maintained counters across sessions into dashboard
   * KPIs. Every counter is optional and treated as 0 when absent, so a session
   * the counter-maintaining function has not yet touched contributes nothing.
   * `minutesRecorded` floors total seconds to whole minutes so a part-minute
   * session never rounds up; `insertedPercent` is 0 when nothing was generated,
   * avoiding a divide-by-zero `NaN`/`Infinity`.
   */
  static counterSummary(
    sessions: IScribeSession[]
  ): IScribeSessionCounterSummary {
    const totalSeconds = sumBy(
      sessions,
      (session) => session.durationSeconds ?? 0
    );
    const notesGenerated = sumBy(sessions, (session) => session.noteCount ?? 0);
    const notesInserted = sumBy(
      sessions,
      (session) => session.insertedNoteCount ?? 0
    );
    const insertedPercent =
      notesGenerated === 0
        ? 0
        : Math.round((notesInserted / notesGenerated) * 100);
    return {
      minutesRecorded: Math.floor(totalSeconds / 60),
      notesGenerated,
      notesInserted,
      insertedPercent,
      estTimeSavedMinutes: notesInserted * estimatedMinutesSavedPerNote,
    };
  }

  /**
   * The session's not-yet-inserted note count, `noteCount − insertedNoteCount`,
   * with each missing counter treated as 0 and the result clamped at 0 so
   * transient counter drift (inserted briefly running ahead of generated) can
   * never surface a negative pending count. A pure read over the
   * function-maintained counters — it never writes them.
   */
  static pendingNoteCount(session: IScribeSession): number {
    return Math.max(
      0,
      (session.noteCount ?? 0) - (session.insertedNoteCount ?? 0)
    );
  }

  /**
   * The VoxDenta session path a token is scoped to: the `{stafferId}/{sessionId}`
   * string sent in the token-mint body and to every SDK call (`start()` /
   * `uploadFile()`). The tenant is excluded — VoxDenta prepends it itself.
   */
  static toVoxSessionPath(stafferId: string, sessionId: string): string {
    return `${stafferId}/${sessionId}`;
  }

  /**
   * A live session that reached VoxDenta may still get its note written there,
   * so failing it also hands it to the recovery sweep, in the same write.
   */
  private static async _failAbandonedSession(
    session: WithRef<IScribeSession>,
    now: Timestamp,
    transaction: Transaction
  ): Promise<void> {
    const reachedVoxDenta =
      session.source === ScribeSessionSource.Live &&
      session.voxSessionPath !== undefined;
    await Firestore.updateDoc(
      session.ref,
      {
        status: ScribeSessionStatus.Failed,
        noteRecovery: reachedVoxDenta
          ? ScribeSession.pendingRecovery(now)
          : undefined,
      },
      transaction
    );
  }

  private static _leaseIsFresh(
    lastActiveAt: Timestamp,
    now: Timestamp
  ): boolean {
    return (
      toMoment(now).diff(toMoment(lastActiveAt), 'seconds') <
      SCRIBE_LEASE_STALE_THRESHOLD_SECONDS
    );
  }

  private static _recoveryIsDue(
    recovery: IScribeNoteRecovery,
    now: Timestamp
  ): boolean {
    return (
      recovery.status === ScribeNoteRecoveryStatus.Pending &&
      toMoment(recovery.nextAttemptAt).isSameOrBefore(toMoment(now))
    );
  }

  private static _recoveryEndReason(
    session: WithRef<IScribeSession>,
    now: Timestamp
  ): string | undefined {
    if (session.deleted) {
      return 'deleted';
    }
    const giveUpAt = toMoment(session.stoppedAt ?? session.createdAt).add(
      SCRIBE_NOTE_RECOVERY_GIVE_UP_HOURS,
      'hours'
    );
    return toMoment(now).isSameOrAfter(giveUpAt) ? 'gaveUp' : undefined;
  }
}
